import React, { useState, useEffect } from 'react';
import { Edit3, Trash2, Plus, Filter } from 'lucide-react';
import { maintenanceService } from '../../services/maintenanceService';
import type { MaintenanceTask, TaskType, TaskStatus, TaskPriority } from '../../types/maintenance';
import TaskForm from './TaskForm';

const TASK_TYPE_LABELS: Record<TaskType, string> = {
  bug_fix: 'Bug',
  improvement: 'Mejora',
  new_feature: 'Nueva func.',
  support: 'Soporte',
  security: 'Seguridad',
  server: 'Servidor',
};

const TASK_TYPE_COLORS: Record<TaskType, string> = {
  bug_fix: 'bg-red-100 text-red-700',
  improvement: 'bg-blue-100 text-blue-700',
  new_feature: 'bg-purple-100 text-purple-700',
  support: 'bg-gray-100 text-gray-700',
  security: 'bg-orange-100 text-orange-700',
  server: 'bg-cyan-100 text-cyan-700',
};

const STATUS_LABELS: Record<TaskStatus, string> = {
  open: 'Abierta',
  in_progress: 'En progreso',
  resolved: 'Resuelta',
  verified: 'Verificada',
};

const STATUS_COLORS: Record<TaskStatus, string> = {
  open: 'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700',
  verified: 'bg-emerald-100 text-emerald-700',
};

const PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Baja',
  normal: 'Normal',
  high: 'Alta',
  critical: 'Critica',
};

const PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: 'text-gray-500',
  normal: 'text-blue-600',
  high: 'text-orange-600',
  critical: 'text-red-600 font-bold',
};

interface TasksListProps {
  contractId: string;
  month?: string; // 'YYYY-MM', if undefined show all
  onTasksChange?: () => void;
}

const TasksList: React.FC<TasksListProps> = ({ contractId, month, onTasksChange }) => {
  const [tasks, setTasks] = useState<MaintenanceTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTask, setEditingTask] = useState<MaintenanceTask | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [filterType, setFilterType] = useState<TaskType | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<TaskStatus | 'all'>('all');
  const [filterPriority, setFilterPriority] = useState<TaskPriority | 'all'>('all');
  const [selectedMonth, setSelectedMonth] = useState(month || new Date().toISOString().slice(0, 7));

  useEffect(() => {
    loadTasks();
  }, [contractId, selectedMonth]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const data = await maintenanceService.getTasksByContractAndMonth(contractId, selectedMonth);
      setTasks(data);
    } catch (err) {
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data: Omit<MaintenanceTask, 'id' | 'created_at' | 'updated_at'>) => {
    if (editingTask) {
      await maintenanceService.updateTask(editingTask.id, data);
    } else {
      await maintenanceService.createTask(data);
    }
    setEditingTask(null);
    setShowForm(false);
    loadTasks();
    onTasksChange?.();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar esta tarea?')) return;
    await maintenanceService.deleteTask(id);
    loadTasks();
    onTasksChange?.();
  };

  const filteredTasks = tasks.filter((t) => {
    if (filterType !== 'all' && t.task_type !== filterType) return false;
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
    return true;
  });

  const totalHours = filteredTasks.reduce((sum, t) => sum + Number(t.hours), 0);

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as TaskType | 'all')}
            className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">Todos los tipos</option>
            {Object.entries(TASK_TYPE_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as TaskStatus | 'all')}
            className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value as TaskPriority | 'all')}
            className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">Todas las prioridades</option>
            {Object.entries(PRIORITY_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>

        <div className="ml-auto">
          <button
            onClick={() => { setEditingTask(null); setShowForm(true); }}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <Plus className="w-4 h-4" />
            Nueva tarea
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Cargando tareas...</div>
      ) : filteredTasks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No hay tareas para este periodo</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-2 font-medium text-gray-600">Fecha</th>
                <th className="text-left py-3 px-2 font-medium text-gray-600">Titulo</th>
                <th className="text-left py-3 px-2 font-medium text-gray-600">Tipo</th>
                <th className="text-right py-3 px-2 font-medium text-gray-600">Horas</th>
                <th className="text-left py-3 px-2 font-medium text-gray-600">Estado</th>
                <th className="text-left py-3 px-2 font-medium text-gray-600">Prioridad</th>
                <th className="text-right py-3 px-2 font-medium text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map((task) => (
                <tr key={task.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-2 px-2 text-gray-600">{task.task_date}</td>
                  <td className="py-2 px-2 font-medium text-gray-900">{task.title}</td>
                  <td className="py-2 px-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TASK_TYPE_COLORS[task.task_type]}`}>
                      {TASK_TYPE_LABELS[task.task_type]}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-right font-mono">{Number(task.hours).toFixed(2)}</td>
                  <td className="py-2 px-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[task.status]}`}>
                      {STATUS_LABELS[task.status]}
                    </span>
                  </td>
                  <td className={`py-2 px-2 text-xs ${PRIORITY_COLORS[task.priority]}`}>
                    {PRIORITY_LABELS[task.priority]}
                  </td>
                  <td className="py-2 px-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => { setEditingTask(task); setShowForm(true); }}
                        className="p-1 text-gray-400 hover:text-blue-600"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(task.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-300">
                <td colSpan={3} className="py-2 px-2 font-semibold text-gray-700">Total</td>
                <td className="py-2 px-2 text-right font-mono font-semibold">{totalHours.toFixed(2)}h</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {showForm && (
        <TaskForm
          contractId={contractId}
          task={editingTask}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditingTask(null); }}
        />
      )}
    </div>
  );
};

export default TasksList;
